// CELPIP-style exam runner: instruction interstitial, one question per screen,
// forward-only navigation, section timer, then a separate review mode with
// trap feedback. The in-exam frame replicates test conventions (timer top
// right, question counter, NEXT bottom right, no going back in Listening);
// review mode is our product surface.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ChevronRight, Clock, Headphones, XCircle } from 'lucide-react';

import { api } from '@/lib/api';
import AssignmentNextCTA from '@/components/AssignmentNextCTA';
import { Button } from '@/components/ui/button';
import DiagramCard from '@/components/DiagramCard';
import { cn } from '@/lib/utils';

const PHASE = { INTRO: 'INTRO', AUDIO: 'AUDIO', RUN: 'RUN', REVIEW: 'REVIEW' };

const TASK_META = {
  listening_p1_problem_solving: {
    section: 'Listening',
    title: 'Part 1: Listening to Problem Solving',
    instructions: [
      'You will hear a conversation about a practical problem.',
      'You will hear the conversation only once.',
      'After the conversation, answer each question. You cannot return to a previous question.',
    ],
    timerSec: 360,
    layout: 'one-by-one',
  },
  listening_p2_daily_life: {
    section: 'Listening',
    title: 'Part 2: Listening to a Daily Life Conversation',
    instructions: [
      'You will hear a conversation from daily life.',
      'You will hear the conversation only once.',
      'Pay attention to how each speaker feels and what each decides to do.',
      'Answer each question. You cannot return to a previous question.',
    ],
    timerSec: 300,
    layout: 'one-by-one',
  },
  listening_p3_information: {
    section: 'Listening',
    title: 'Part 3: Listening for Information',
    instructions: [
      'You will hear an informational conversation.',
      'You will hear the conversation only once.',
      'Listen for steps, conditions, and numbers.',
      'Answer each question. You cannot return to a previous question.',
    ],
    timerSec: 320,
    layout: 'one-by-one',
  },
  listening_p4_news_item: {
    section: 'Listening',
    title: 'Part 4: Listening to a News Item',
    instructions: [
      'You will hear a local news report.',
      'You will hear the report only once.',
      'Separate what happened from background and from what is planned.',
      'Answer each question. You cannot return to a previous question.',
    ],
    timerSec: 280,
    layout: 'one-by-one',
  },
  listening_p5_discussion: {
    section: 'Listening',
    title: 'Part 5: Listening to a Discussion',
    instructions: [
      'You will hear a discussion among three speakers.',
      'You will hear the discussion only once.',
      'Track which speaker holds which view, and what the group finally decides.',
      'Answer each question. You cannot return to a previous question.',
    ],
    timerSec: 360,
    layout: 'one-by-one',
  },
  listening_p6_viewpoints: {
    section: 'Listening',
    title: 'Part 6: Listening to Viewpoints',
    instructions: [
      'You will hear one speaker present several viewpoints on an issue.',
      'You will hear the talk only once.',
      'Separate the viewpoints the speaker reports from the position the speaker holds.',
      'Answer each question. You cannot return to a previous question.',
    ],
    timerSec: 330,
    layout: 'one-by-one',
  },
  reading_p1_correspondence: {
    section: 'Reading',
    title: 'Part 1: Reading Correspondence',
    instructions: [
      'Read the message on the left.',
      'Answer all the questions. You may change your answers before moving on.',
      'Some questions ask you to complete the response to the message.',
    ],
    timerSec: 660,
    layout: 'passage-all',
  },
  reading_p2_diagram: {
    section: 'Reading',
    title: 'Part 2: Reading to Apply a Diagram',
    instructions: [
      'Study the diagram and read the message beside it.',
      'The message refers to information in the diagram.',
      'Choose the option that correctly applies the diagram to each question.',
      'You may change your answers before submitting.',
    ],
    timerSec: 540,
    layout: 'passage-all',
  },
  reading_p3_information: {
    section: 'Reading',
    title: 'Part 3: Reading for Information',
    instructions: [
      'Read the passage with paragraphs A to D.',
      'Decide which paragraph contains the information in each statement.',
      'If the information is not in any paragraph, choose "Not given".',
      'Work quickly: this part is the most time-pressured in the Reading section.',
    ],
    timerSec: 600,
    layout: 'passage-all',
  },
  reading_p4_viewpoints: {
    section: 'Reading',
    title: 'Part 4: Reading for Viewpoints',
    instructions: [
      'Read the article presenting several viewpoints.',
      'Track who holds each position, and their reason for it.',
      'Answer all the questions. You may change your answers before moving on.',
    ],
    timerSec: 780,
    layout: 'passage-all',
  },
};

const DEFAULT_META = {
  section: 'Practice',
  title: 'Practice set',
  instructions: ['Answer each question.', 'You cannot return to a previous question.'],
  timerSec: 300,
  layout: 'one-by-one',
};

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function ExamRunner() {
  const { exam, taskType } = useParams();
  const [searchParams] = useSearchParams();
  const setParam = searchParams.get('set');
  const navigate = useNavigate();
  const meta = TASK_META[taskType] || DEFAULT_META;

  const [set, setSet] = useState(null);
  const [phase, setPhase] = useState(PHASE.INTRO);
  const [qIdx, setQIdx] = useState(0);
  const [picked, setPicked] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(meta.timerSec);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFailed, setAudioFailed] = useState(false); // true when audio truly cannot play (missing file, network / decoder error). Only this state exposes a "Continue to questions" fallback so the student is not permanently locked out.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false); // true when the browser blocked autoplay (iOS Safari policy) but the clip DID load. Surfaces a Tap-to-play button whose click is a user gesture — no exam-integrity bypass.
  const [sessionSets, setSessionSets] = useState([]);  // ordered set_ids for continuous flow
  const audioElRef = useRef(null);
  const audioAdvancedRef = useRef(false);
  const submittingRef = useRef(false);
  const pickedRef = useRef({});
  const hasAudio = !!set?.stimulus?.audio_ref;
  const isListening = meta.section === 'Listening';

  const loadSet = useCallback(async () => {
    setError('');
    setResult(null);
    setPicked({});
    pickedRef.current = {};
    setQIdx(0);
    setPhase(PHASE.INTRO);
    setSecondsLeft(meta.timerSec);
    setSet(null);
    setAudioUrl(null);
    setAudioFailed(false);
    setAutoplayBlocked(false);
    audioAdvancedRef.current = false;
    submittingRef.current = false;
    try {
      const res = await api.practiceSets(exam, taskType, 1, setParam);
      setSet(res.data.sets[0]);
    } catch (err) {
      setError(err.response?.data?.detail || 'No practice set available.');
    }
  }, [exam, taskType, meta.timerSec, setParam]);

  useEffect(() => {
    loadSet();
  }, [loadSet]);

  // The ordered set list for this task type powers the continuous session: after
  // a set, the student flows straight to the next one without bouncing back.
  useEffect(() => {
    api.practiceSetList(exam, taskType)
      .then((r) => setSessionSets((r.data.sets || []).map((s) => s.set_id)))
      .catch(() => setSessionSets([]));
  }, [exam, taskType]);

  const submit = useCallback(async (currentSet) => {
    if (submittingRef.current || !currentSet) return;
    submittingRef.current = true;
    try {
      const responses = currentSet.questions.map((q) => ({
        qid: q.qid,
        answer_index: pickedRef.current[q.qid] ?? null,
      }));
      const res = await api.submitPractice(exam, { set_id: currentSet.set_id, responses });
      setResult(res.data);
      setPhase(PHASE.REVIEW);
    } catch (err) {
      setError(err.response?.data?.detail || 'Submission failed.');
      submittingRef.current = false;
    }
  }, [exam]);

  // Section timer: counts down during RUN, auto-submits at zero (exam behavior).
  useEffect(() => {
    if (phase !== PHASE.RUN) return undefined;
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          submit(set);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, set, submit]);

  // Imperative playback the moment the AUDIO phase mounts: `<audio autoPlay>`
  // fires neither `ended` nor `error` when the browser blocks autoplay
  // (iOS Safari + hardened Chrome), which is exactly how students used to end
  // up stuck on "Listen carefully…" with no way forward. Calling
  // `ref.play().catch()` surfaces the block, we flip `audioFailed`, and the
  // always-visible Continue button below carries them into the questions.
  // Imperative playback the moment the AUDIO phase mounts: `<audio autoPlay>`
  // fires neither `ended` nor `error` when the browser blocks autoplay
  // (iOS Safari + hardened Chrome). Calling `ref.play().catch()` distinguishes
  // the two failure modes:
  //   • NotAllowedError → autoplay policy block; clip is loaded, a user
  //     gesture will unblock it. Flip `autoplayBlocked` so we render a
  //     Tap-to-play button (NOT a skip-to-questions button — this is a real
  //     listening exam, students must not be able to bypass the audio).
  //   • Anything else (NotSupportedError, media decode error, network fail)
  //     → the clip genuinely can't play; flip `audioFailed` so we surface
  //     the transcript fallback + Continue button on the next screen.
  useEffect(() => {
    if (phase !== PHASE.AUDIO || !audioUrl) return undefined;
    const el = audioElRef.current;
    if (!el) return undefined;
    let cancelled = false;
    const play = async () => {
      try { await el.play(); }
      catch (err) {
        if (cancelled) return;
        if (err && err.name === 'NotAllowedError') setAutoplayBlocked(true);
        else setAudioFailed(true);
      }
    };
    play();
    return () => { cancelled = true; };
  }, [phase, audioUrl]);

  // User-gesture retry: when the student taps the button after autoplay was
  // blocked. Playback under a user gesture bypasses the policy for this
  // element; a subsequent failure indicates a true media error.
  const tapToPlay = async () => {
    const el = audioElRef.current;
    if (!el) return;
    try {
      await el.play();
      setAutoplayBlocked(false);
    } catch (err) {
      if (err && err.name === 'NotAllowedError') return; // still blocked, let them tap again
      setAutoplayBlocked(false);
      setAudioFailed(true);
    }
  };


  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-100">
        <p className="text-slate-600">{error}</p>
        <Link to={`/${exam}/practice`} className="text-sm font-medium text-blue-800 hover:underline">
          Back to practice
        </Link>
      </div>
    );
  }
  if (!set) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500">Preparing your set…</div>;
  }

  const question = set.questions?.[qIdx];
  const isLast = qIdx === ((set.questions?.length ?? 1) - 1);
  const answered = question && picked[question.qid] != null;
  const answeredCount = (set.questions ?? []).filter((q) => picked[q.qid] != null).length;
  const allAnswered = (set.questions?.length ?? 0) > 0 && answeredCount === set.questions.length;
  const resultFor = (qid) => result?.results.find((r) => r.qid === qid);

  // Continuous-session position: where this set sits in the task type, and the
  // next one to flow into. Navigating ?set= reloads the runner on the next set.
  const curId = set?.set_id || setParam;
  const pos = sessionSets.indexOf(curId);
  const nextSetId = pos >= 0 && pos < sessionSets.length - 1 ? sessionSets[pos + 1] : null;
  const goNextSet = () => { if (nextSetId) navigate(`/${exam}/practice/${taskType}?set=${nextSetId}`); };

  const pick = (qid, oi) => {
    setPicked((p) => {
      const next = { ...p, [qid]: oi };
      pickedRef.current = next;
      return next;
    });
  };

  // ---------- exam chrome (shared) ----------
  const ExamHeader = ({ showTimer }) => (
    <header className="bg-blue-900 text-white">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-3">
        <div className="flex items-center gap-3">
          <Headphones className="h-4 w-4 opacity-80" />
          <div>
            <p className="text-[11px] uppercase tracking-wider text-blue-200">{exam.toUpperCase()} practice • {meta.section}</p>
            <p className="text-sm font-semibold">{meta.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {pos >= 0 && sessionSets.length > 1 && (
            <span className="text-xs font-medium text-blue-200" data-testid="exam-set-pos">Set {pos + 1} of {sessionSets.length}</span>
          )}
          {phase === PHASE.RUN && (
            <span className="text-xs font-medium text-blue-100" data-testid="exam-counter">
              {meta.layout === 'passage-all'
                ? `${answeredCount} of ${set.questions.length} answered`
                : `Question ${qIdx + 1} of ${set.questions.length}`}
            </span>
          )}
          {showTimer && (
            <span
              className={cn(
                'flex items-center gap-1.5 rounded bg-blue-950/60 px-2.5 py-1 text-sm font-semibold tabular-nums',
                secondsLeft <= 30 && 'bg-red-600'
              )}
              data-testid="exam-timer"
            >
              <Clock className="h-3.5 w-3.5" />
              {formatTime(secondsLeft)}
            </span>
          )}
        </div>
      </div>
    </header>
  );
  // Advance from the audio screen exactly once — funnels onEnded/onError
  // and manual "Continue" clicks through the same idempotent transition.
  const advanceFromAudio = () => {
    if (audioAdvancedRef.current) return;
    audioAdvancedRef.current = true;
    setPhase(PHASE.RUN);
  };

  const startSet = async () => {
    // Listening always shows the audio stage: the real recording when it is
    // available, otherwise an inert "recording slot" so the screen still reads
    // as a listening task. Audio is rendered at deploy; the clip drops straight
    // into this slot once it exists, with no UI change needed.
    if (isListening) {
      if (hasAudio) {
        try {
          const res = await api.contentAudioBlob(set.stimulus.audio_ref);
          setAudioUrl(URL.createObjectURL(res.data));
        } catch {
          // audio not on disk yet → mark failed, show the inert slot + transcript fallback
          setAudioFailed(true);
        }
      }
      setPhase(PHASE.AUDIO);
      return;
    }
    setPhase(PHASE.RUN);
  };
  // ---------- AUDIO: plays exactly once, no controls, then questions ----------
  if (phase === PHASE.AUDIO) {
    return (
      <div className="min-h-screen bg-slate-100">
        <ExamHeader showTimer={false} />
        <main className="mx-auto max-w-3xl px-6 py-24">
          <div className="flex flex-col items-center gap-6 border border-slate-300 bg-white p-12 shadow-sm">
            <span className={cn('flex h-14 w-14 items-center justify-center rounded-full', audioUrl && !audioFailed ? 'bg-blue-900' : 'bg-slate-300')}>
              <Headphones className={cn('h-6 w-6 text-white', audioUrl && !audioFailed && !autoplayBlocked && 'animate-pulse')} />
            </span>
            {audioUrl && !audioFailed ? (
              <>
                <div className="text-center">
                  <p className="font-semibold text-slate-900" data-testid="audio-playing">
                    {autoplayBlocked ? 'Tap to start the recording.' : 'Listen carefully.'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    {autoplayBlocked
                      ? 'Your browser blocked autoplay. Tap the button below to begin — the recording still plays only once.'
                      : 'The recording plays only once. Questions follow immediately.'}
                  </p>
                </div>
                <audio
                  ref={audioElRef}
                  src={audioUrl}
                  data-testid="exam-audio"
                  onEnded={advanceFromAudio}
                  onError={() => setAudioFailed(true)}
                />
                {autoplayBlocked && (
                  <Button
                    onClick={tapToPlay}
                    data-testid="audio-tap-to-play"
                    className="bg-blue-900 px-8 hover:bg-blue-800"
                  >
                    Tap to play
                  </Button>
                )}
              </>
            ) : (
              <>
                <div className="text-center">
                  <p className="font-semibold text-slate-900" data-testid="audio-pending">
                    {audioFailed ? 'Audio couldn’t play automatically.' : 'The recording plays here.'}
                  </p>
                  <p className="mt-1 max-w-md text-sm text-slate-500">
                    {audioFailed
                      ? 'Your browser blocked autoplay or the clip failed to load. Continue to the questions — you can review the transcript on the next screen.'
                      : 'On the live exam the clip plays once and the questions follow. Audio for this set is being enabled, it will play automatically here once it is ready.'}
                  </p>
                </div>
                <Button onClick={advanceFromAudio} className="bg-blue-900 px-8 hover:bg-blue-800" data-testid="audio-continue">
                  Continue to questions
                </Button>
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ---------- INTRO ----------
  if (phase === PHASE.INTRO) {
    return (
      <div className="min-h-screen bg-slate-100">
        <ExamHeader showTimer={false} />
        <main className="mx-auto max-w-3xl px-6 py-12">
          <div className="border border-slate-300 bg-white p-10 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-blue-900">Instructions</p>
            <h1 className="mt-2 text-xl font-semibold text-slate-900">{meta.title}</h1>
            <ul className="mt-6 space-y-3">
              {meta.instructions.map((line, i) => (
                <li key={i} className="flex gap-3 text-[15px] leading-6 text-slate-700">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />
                  {line}
                </li>
              ))}
              <li className="flex gap-3 text-[15px] leading-6 text-slate-700">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-900" />
                You have {formatTime(meta.timerSec)} for this set.
              </li>
            </ul>
            <div className="mt-10 flex items-center justify-between">
              <Link to={`/${exam}/practice`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800" data-testid="exam-exit">
                <ArrowLeft className="h-4 w-4" /> Exit
              </Link>
              <Button
                size="lg"
                className="bg-blue-900 px-10 hover:bg-blue-800"
                data-testid="exam-start"
                onClick={startSet}
              >
                START
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ---------- RUN: passage with all questions (Reading parts) ----------
  if (phase === PHASE.RUN && meta.layout === 'passage-all') {
    return (
      <div className="min-h-screen bg-slate-100">
        <ExamHeader showTimer />
        <main className="mx-auto max-w-6xl px-6 py-8">
          <div className="grid gap-6 lg:grid-cols-2">
            <div>
              <div className="lg:sticky lg:top-6 space-y-4">
                {set.stimulus?.diagram && <DiagramCard diagram={set.stimulus.diagram} />}
                <div className="border border-slate-300 bg-white shadow-sm">
                  <div className="border-b border-slate-200 bg-slate-50 px-5 py-2.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {set.stimulus?.diagram ? 'Message' : 'Reading passage'}
                    </p>
                  </div>
                  <div className={cn('overflow-y-auto px-6 py-5', set.stimulus?.diagram ? 'max-h-[38vh]' : 'max-h-[70vh]')}>
                    <p className="whitespace-pre-wrap text-[15px] leading-7 text-slate-800">
                      {set.stimulus?.text || set.stimulus?.transcript}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-5">
              {set.questions.map((q, qi) => (
                <div key={q.qid} className="border border-slate-300 bg-white shadow-sm" data-testid={`exam-question-${q.qid}`}>
                  <div className="border-b border-slate-200 px-5 py-3">
                    <p className="text-[15px] font-medium leading-6 text-slate-900">{qi + 1}. {q.prompt}</p>
                  </div>
                  <div className="space-y-1 px-3 py-3">
                    {q.options.map((opt, oi) => {
                      const chosen = picked[q.qid] === oi;
                      return (
                        <button
                          key={oi}
                          type="button"
                          data-testid={`exam-option-${q.qid}-${oi}`}
                          onClick={() => pick(q.qid, oi)}
                          className={cn(
                            'flex w-full items-start gap-3 rounded-sm border px-3 py-2 text-left text-sm leading-6 transition-colors',
                            chosen
                              ? 'border-blue-900 bg-blue-50 text-slate-900'
                              : 'border-transparent text-slate-700 hover:bg-slate-50'
                          )}
                        >
                          <span
                            className={cn(
                              'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold',
                              chosen ? 'border-blue-900 bg-blue-900 text-white' : 'border-slate-300 text-slate-400'
                            )}
                          >
                            {String.fromCharCode(65 + oi)}
                          </span>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="flex justify-end pt-2">
                <Button
                  size="lg"
                  className="bg-blue-900 px-10 hover:bg-blue-800"
                  disabled={!allAnswered}
                  data-testid="exam-next"
                  onClick={() => submit(set)}
                >
                  SUBMIT
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ---------- RUN: one question per screen, forward only (Listening parts) ----------
  if (phase === PHASE.RUN) {
    return (
      <div className="min-h-screen bg-slate-100">
        <ExamHeader showTimer />
        <main className="mx-auto max-w-5xl px-6 py-8">
          <div className="grid gap-6 lg:grid-cols-5">
            {/* With real audio the transcript stays HIDDEN during questions
                (exam behavior). If audio playback FAILED (autoplay blocked,
                network drop, file missing) we surface the transcript as a
                lifeline — an unanswerable blank listening item is worse than
                trading a little exam fidelity for a usable practice session. */}
            {(!hasAudio || audioFailed) && (set.stimulus?.transcript || set.stimulus?.text) && (
              <div className="lg:col-span-2">
                <div className="border border-slate-300 bg-white shadow-sm">
                  <div className="border-b border-slate-200 bg-slate-50 px-5 py-2.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Conversation
                    </p>
                  </div>
                  <div className="max-h-[420px] overflow-y-auto px-5 py-4">
                    <p className="whitespace-pre-wrap text-sm leading-7 text-slate-700">
                      {set.stimulus.transcript || set.stimulus.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className={cn('lg:col-span-3', (hasAudio || !(set.stimulus?.transcript || set.stimulus?.text)) && 'lg:col-span-5 mx-auto w-full max-w-3xl')}>
              <div className="border border-slate-300 bg-white shadow-sm" data-testid={`exam-question-${question.qid}`}>
                <div className="border-b border-slate-200 px-6 py-4">
                  <p className="text-[15px] font-medium leading-6 text-slate-900">{question.prompt}</p>
                </div>
                <div className="space-y-1 px-4 py-4">
                  {question.options.map((opt, oi) => {
                    const chosen = picked[question.qid] === oi;
                    return (
                      <button
                        key={oi}
                        type="button"
                        data-testid={`exam-option-${question.qid}-${oi}`}
                        onClick={() => pick(question.qid, oi)}
                        className={cn(
                          'flex w-full items-start gap-3 rounded-sm border px-4 py-3 text-left text-[15px] leading-6 transition-colors',
                          chosen
                            ? 'border-blue-900 bg-blue-50 text-slate-900'
                            : 'border-transparent text-slate-700 hover:bg-slate-50'
                        )}
                      >
                        <span
                          className={cn(
                            'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold',
                            chosen ? 'border-blue-900 bg-blue-900 text-white' : 'border-slate-300 text-slate-400'
                          )}
                        >
                          {String.fromCharCode(65 + oi)}
                        </span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button
                  size="lg"
                  className="bg-blue-900 px-10 hover:bg-blue-800"
                  disabled={!answered}
                  data-testid="exam-next"
                  onClick={() => (isLast ? submit(set) : setQIdx((i) => i + 1))}
                >
                  NEXT <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-3 text-right text-xs text-slate-400">You cannot return to a previous question.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ---------- REVIEW: our product surface ----------
  return (
    <div className="min-h-screen bg-slate-100">
      <ExamHeader showTimer={false} />
      <main className="mx-auto max-w-3xl px-6 py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Review</h1>
            <p className="mt-1 text-sm text-slate-500">
              Every wrong option is a designed trap. Learn the trap, not just the answer.
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-semibold tabular-nums text-slate-900" data-testid="exam-score">
              {result.n_correct}<span className="text-slate-400">/{result.n_total}</span>
            </p>
            <p className="text-xs uppercase tracking-wide text-slate-500">correct</p>
          </div>
        </div>

        <div className="mt-6 space-y-5">
          {set.questions.map((q, qi) => {
            const res = resultFor(q.qid);
            return (
              <div key={q.qid} className="border border-slate-200 bg-white shadow-sm" data-testid={`review-${q.qid}`}>
                <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-4">
                  {res?.correct ? (
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  ) : (
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
                  )}
                  <p className="text-[15px] font-medium leading-6 text-slate-900">{qi + 1}. {q.prompt}</p>
                </div>
                <div className="space-y-1 px-5 py-3">
                  {q.options.map((opt, oi) => {
                    const isAnswer = oi === res?.answer_index;
                    const wasYoursWrong = !res?.correct && oi === res?.your_answer;
                    return (
                      <p
                        key={oi}
                        className={cn(
                          'rounded-sm px-3 py-1.5 text-sm leading-6 text-slate-600',
                          isAnswer && 'bg-emerald-50 font-medium text-emerald-900',
                          wasYoursWrong && 'bg-red-50 text-red-900'
                        )}
                      >
                        {String.fromCharCode(65 + oi)}. {opt}
                      </p>
                    );
                  })}
                </div>
                {(res?.explanation || res?.why_yours_attracts) && (
                  <div className="border-t border-slate-100 bg-slate-50 px-5 py-4 text-sm leading-6">
                    {res.trap_label && (
                      <span className="mb-2 inline-flex items-center gap-1.5 rounded bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">
                        Trap: {res.trap_label}
                      </span>
                    )}
                    {res.explanation && <p className="text-slate-700">{res.explanation}</p>}
                    {res.why_yours_attracts && (
                      <p className="mt-2 text-slate-600">
                        <span className="font-semibold text-slate-800">Why your answer attracted you: </span>
                        {res.why_yours_attracts}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8">
          <AssignmentNextCTA />
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
            data-testid="review-exit"
            onClick={() => navigate(`/${exam}/practice`)}
          >
            <ArrowLeft className="h-4 w-4" /> All task types
          </button>
          {nextSetId ? (
            <Button size="lg" className="bg-blue-900 px-8 hover:bg-blue-800" data-testid="review-next-set" onClick={goNextSet}>
              Next set <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button size="lg" className="bg-blue-900 px-8 hover:bg-blue-800" data-testid="review-next-set"
              onClick={() => (sessionSets.length > 0 ? navigate(`/${exam}/sets/${taskType}`) : loadSet())}>
              {sessionSets.length > 0 ? 'All sets done, back to list' : 'Next set'}
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
